import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { AnalystReadingField } from '@/components/admin/protocoles/reading/analyst-reading-field';
import { ANALYST_PASS_TAB } from '@/lib/analyst-reading';
import { cn } from '@/lib/utils';
import type { AnalystPass, ProtocolAnalyst, ProtocolStepDraft } from '@matter_extractor/shared';
import { ANALYST_PASSES } from '@matter_extractor/shared';
import { type KeyboardEvent, useId } from 'react';

/**
 * Les deux onglets « Passe 1 · les fonctions » et « Passe 2 · le style » et la
 * lecture que chacun montre (analyste.md §3.3, §3.5 ; planche `AnalysteA` et son
 * état `AnalysteAEtats`). L'onglet choisi change ENSEMBLE la lecture sous lui et
 * l'aperçu de droite - l'état de l'onglet est celui de la section, qui monte l'aperçu.
 *
 * ## A real tablist, which writes nothing in the address
 *
 * The tabs switch a panel in place, like the four of the review panel
 * (`sessions/review/review-panel.tsx`, the precedent): `role="tablist"`, the active
 * tab alone in the tab order (a roving `tabIndex`), the arrows - and `Home` and `End`
 * - walking them. The pass is a posture of the reader and is not in the URL: `?vue=`
 * names the section, and an address that pinned the tab would carry nothing a
 * thread's reader could not choose in one click.
 *
 * ## Both readings stay MOUNTED, the other one `hidden`
 *
 * The convention of the three views of the editor (review of !474): a painted field
 * keeps the caret it had and the refusal it said across a trip to the other tab, and
 * a tab that unmounted its field would hand back a field reset to its defaults after
 * every glance at the other pass. Each carries its own marks, since the strict mode
 * of Playwright refuses two matches; the panel on the right is the one that is drawn
 * for the ACTIVE pass only.
 */

export interface AnalystReadingsProps {
  readonly analyst: ProtocolAnalyst;
  /** The steps the readings cite and name - the draft's, or the version « Voir » shows. */
  readonly steps: readonly ProtocolStepDraft[];
  readonly readOnly: boolean;
  /** The active tab. */
  readonly pass: AnalystPass;
  readonly onPass: (pass: AnalystPass) => void;
  /** The id of the yellow encart when it is drawn: an empty reading is described by it. */
  readonly encartId: string | null;
  readonly onChange: (next: ProtocolAnalyst) => void;
}

/** The tab a key walks to from the one at `at`, or null for a key that does not walk: the arrows wrap, Home and End go to the ends. */
function tabTargetOf(key: string, at: number, count: number): number | null {
  switch (key) {
    case 'ArrowRight':
      return (at + 1) % count;
    case 'ArrowLeft':
      return (at + count - 1) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

/** The mark of each tab. */
const TAB_IDS = {
  1: PROTOCOL_TEST_IDS.analystTabPass1,
  2: PROTOCOL_TEST_IDS.analystTabPass2,
} as const;

export function AnalystReadings({
  analyst,
  steps,
  readOnly,
  pass,
  onPass,
  encartId,
  onChange,
}: AnalystReadingsProps) {
  const base = useId();
  const tabId = (one: AnalystPass) => `${base}-tab-${one}`;
  const panelId = (one: AnalystPass) => `${base}-panel-${one}`;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const target = tabTargetOf(event.key, ANALYST_PASSES.indexOf(pass), ANALYST_PASSES.length);
    const next = target === null ? undefined : ANALYST_PASSES[target];
    if (next === undefined) return;
    event.preventDefault();
    onPass(next);
    document.getElementById(tabId(next))?.focus();
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div
        role="tablist"
        aria-label="Lecture de chaque passe"
        data-testid={PROTOCOL_TEST_IDS.analystTabs}
        onKeyDown={onKeyDown}
        className="flex gap-6 border-gray-200 border-b"
      >
        {ANALYST_PASSES.map((one) => {
          const selected = one === pass;
          return (
            <button
              key={one}
              id={tabId(one)}
              type="button"
              role="tab"
              data-testid={TAB_IDS[one]}
              aria-selected={selected}
              aria-controls={panelId(one)}
              tabIndex={selected ? 0 : -1}
              onClick={() => onPass(one)}
              className={cn(
                '-mb-px inline-flex h-10 cursor-pointer items-center whitespace-nowrap border-b-[3px] px-1 font-semibold text-[15px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit',
                selected
                  ? 'border-bleu-nuit text-bleu-nuit'
                  : 'border-transparent text-gray-600 hover:text-bleu-nuit',
              )}
            >
              {ANALYST_PASS_TAB[one]}
            </button>
          );
        })}
      </div>
      {ANALYST_PASSES.map((one) => (
        <div
          key={one}
          id={panelId(one)}
          role="tabpanel"
          aria-labelledby={tabId(one)}
          hidden={one !== pass}
          className="min-w-0"
        >
          <AnalystReadingField
            pass={one}
            reading={one === 1 ? analyst.functionsReading : analyst.styleReading}
            steps={steps}
            readOnly={readOnly}
            encartId={encartId}
            onChange={(next) =>
              onChange(
                one === 1
                  ? { ...analyst, functionsReading: next }
                  : { ...analyst, styleReading: next },
              )
            }
          />
        </div>
      ))}
    </div>
  );
}
